import { defaultFetchHeaders, fetchJson } from "../../utils";

export type MobilePlatform = "ios" | "android";

export type MobileVersionPolicy = {
  platform: MobilePlatform;
  latestBuild: number;
  minimumBuild: number;
  candidateMinimumBuild: number | null;
  storeVerifiedBuild: number | null;
  enforcementEnabled: boolean;
  storeUrl: string;
  releaseNotes: string;
  updatedAt: string;
};

export type MobilePolicyAudit = {
  id: string;
  platform: MobilePlatform;
  action: string;
  previousValues: Record<string, unknown>;
  newValues: Record<string, unknown>;
  actorEmail: string | null;
  createdAt: string;
};

const backend = process.env.REACT_APP_BACKEND_URL || "";
const policyBase = `${backend}/mobile/version-policy`;

async function request<T>(path: string, method = "GET", body?: object): Promise<T> {
  return fetchJson<T>(
    `${policyBase}${path}`,
    {
      method,
      credentials: "include",
      ...(method === "GET" ? { cache: "no-store" as RequestCache } : {}),
      headers: {
        ...(await defaultFetchHeaders()),
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    },
    "Mobile app policy request failed",
  );
}

export async function fetchMobilePolicies(): Promise<MobileVersionPolicy[]> {
  return (await request<{ items: MobileVersionPolicy[] }>("/admin")).items;
}

export async function updateMobileRelease(
  platform: MobilePlatform,
  values: Pick<MobileVersionPolicy, "latestBuild" | "storeUrl" | "releaseNotes">,
): Promise<MobileVersionPolicy> {
  return request(`/${platform}/release`, "POST", values);
}

export async function verifyMobileBuild(platform: MobilePlatform, build: number) {
  return request<MobileVersionPolicy>(`/${platform}/verify`, "POST", { build });
}

export async function stageMobileMinimum(platform: MobilePlatform, build: number) {
  return request<MobileVersionPolicy>(`/${platform}/minimum/stage`, "POST", { build });
}

export async function activateMobileMinimum(
  platform: MobilePlatform,
  build: number,
  confirmation: string,
) {
  return request<MobileVersionPolicy>(`/${platform}/minimum/activate`, "POST", {
    build,
    confirmation,
  });
}

export async function disableMobileEnforcement(platform: MobilePlatform) {
  return request<MobileVersionPolicy>(`/${platform}/enforcement/disable`, "POST");
}

export async function rollbackMobileMinimum(
  platform: MobilePlatform,
  minimumBuild: number,
  confirmation: string,
) {
  return request<MobileVersionPolicy>(`/${platform}/minimum/rollback`, "POST", {
    minimumBuild,
    confirmation,
  });
}

export async function correctMobilePolicy(
  platform: MobilePlatform,
  build: number,
  confirmation: string,
) {
  return request<MobileVersionPolicy>(`/${platform}/emergency/correct`, "POST", {
    build,
    confirmation,
  });
}

export async function fetchMobilePolicyAudit(
  platform: MobilePlatform,
  offset = 0,
  limit = 25,
): Promise<{ items: MobilePolicyAudit[]; hasMore: boolean }> {
  const query = new URLSearchParams({
    platform,
    offset: String(offset),
    limit: String(limit),
  });
  return request(`/audit?${query.toString()}`);
}
